import React, { useContext } from "react";
import { Button, Dialog } from "@cmsgov/ds-medicare-gov";
import { useTranslate } from "../helpers";
import { AutocompleteDrug } from "../@types";
import { FormattedHTMLMessage } from "react-intl-phraseapp";
import { AppContext } from "../app/store";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
} from "../app/contexts/Analytics/types";

export const userGenericDialogGenericAvailableTestId =
  "user-generic-dlg-generic-available";
interface UseGenericDialogProps
  extends Omit<React.ComponentProps<typeof Dialog>, "children"> {
  // brand name drug that has a generic alternative
  brandDrug: AutocompleteDrug;
  onSelectDrug: (selectedDrug: AutocompleteDrug) => void;
}

/**
 * UseGenericDialog is used to prompt the user if they like to use the generic drug.
 */
const UseGenericDialog: React.FunctionComponent<UseGenericDialogProps> = props => {
  const t = useTranslate();
  const { dispatch } = useContext(AppContext);
  const brandDrug = props.brandDrug;

  return (
    <Dialog
      headerClassName="UseGenericDialog__promptHeader"
      heading={t("prescription.generic_prompt_header2")}
      actions={[
        <Button
          variation="solid"
          key="add-generic-button"
          className="e2e-add-generic-drug-btn"
          onClick={(): void => {
            dispatch({
              type: AnalyticsActionType.SEND_TEALIUM_EVENT,
              settings: {
                event_action: "Find Plans - Add Drugs",
                event_label: "Add Generic",
              },
            });
            dispatch({
              type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
              settings: {
                button: {
                  buttonStyle: AnalyticsButtonStyle.PRIMARY,
                  buttonType: AnalyticsButtonType.BUTTON,
                  text: t("prescription.add_generic"),
                },
              },
            });
            const genericDrug = brandDrug.generic as AutocompleteDrug;
            genericDrug.is_generic = true;
            props.onSelectDrug(genericDrug);
          }}
        >
          {t("prescription.add_generic")}
        </Button>,
        <Button
          key="add-brand-button"
          className="e2e-add-brand-name-drug-btn ds-u-margin-left--2"
          onClick={(): void => {
            dispatch({
              type: AnalyticsActionType.SEND_TEALIUM_EVENT,
              settings: {
                event_action: "Find Plans - Add Drugs",
                event_label: "Add Brand",
              },
            });
            dispatch({
              type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
              settings: {
                button: {
                  buttonStyle: AnalyticsButtonStyle.DEFAULT,
                  buttonType: AnalyticsButtonType.BUTTON,
                  text: t("prescription.add_brand_instead"),
                },
              },
            });
            props.onSelectDrug(brandDrug);
          }}
        >
          {t("prescription.add_brand_instead2")}
        </Button>,
      ]}
      {...props}
    >
      <p
        className="ds-u-margin-top--0 addGeneric__text"
        data-testid={userGenericDialogGenericAvailableTestId}
      >
        <FormattedHTMLMessage
          id="prescription.generic_available2"
          tagName="span"
          values={{
            brand: brandDrug.name,
            generic: (brandDrug as AutocompleteDrug).generic?.name,
          }}
        />
      </p>
      <p className="addGeneric__text">
        <FormattedHTMLMessage
          id="prescription.add_generic_instead2"
          tagName="span"
          values={{
            generic: (brandDrug as AutocompleteDrug).generic?.name,
          }}
        />
      </p>
    </Dialog>
  );
};

export default UseGenericDialog;
